<template>
  <div class="expression-input-with-template">
    <input-state-toggle
      :value="selectedMode"
      :states="['template', 'expression']"
      v-slot="{ state: mode }"
      @update:value="setMode($event)"
    >
      <span>{{ $t(`expressionInputTypes.${mode}`) }}</span>
    </input-state-toggle>

    <expression-input
      v-if="mode === 'expression' || mode === 'tree'"
      :expression="expression"
      :tree="expressionTree"
      :parse-context="expressionParseContext"
      editor="external"
      :editor-open="expressionEditorVisible"
      @update:editor-open="setEditorOpen"
      @update:input-mode="setMode"
      @update:expression="emitUpdate"
    />
    <expression-template-input
      v-if="mode === 'template'"
      :expression="expression"
      :tree="expressionTree"
      :expression-parse-context="expressionParseContext"
      editor="external"
      :editor-open="expressionEditorVisible"
      @update:editor-open="setEditorOpen"
      @update:input-mode="setMode"
      @update:expression="emitUpdate"
    />

    <expression-editor
      :visible="expressionEditorVisible"
      :tree="expressionTree"
      :expression="expression"
      :parse-context="expressionParseContext"
      :input-mode="mode"
      @update:expression="emitUpdate"
      @update:input-mode="setMode"
      @close="setEditorOpen(false)"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";

import InputStateToggle from "@core/input/InputStateToggle.vue";
import ExpressionInput from "./ExpressionInput.vue";
import ExpressionEditor from "./ExpressionEditor.vue";
import ExpressionTemplateInput from "./ExpressionTemplateInput.vue";

import { useExpressionTree } from "./ExpressionTree";
import { useSettingsStore } from "@editor/store/settings.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { useIndexStore } from "@editor/items/index/indices.store";
import { useTargetStore } from "@editor/items/target/targets.store";

import type { ExpressionTree } from "./ExpressionTree";
import type { ExpressionParseContext } from "@editor/items/effect/Expression";

const props = withDefaults(defineProps<{
  expression: string;
  parseContext: ExpressionParseContext;
  tree?: ExpressionTree;
  inputMode?: "template" | "expression" | "tree";
}>(), {
  inputMode: "template"
});

const emit = defineEmits<{
  (e: "update:expression", value: string): void;
  (e: "update:input-mode", value: "template" | "expression" | "tree"): void;
}>();

const settings = useSettingsStore();
const resourceStore = useResourceStore();
const indexStore = useIndexStore();
const targetStore = useTargetStore();

const expressionParseContext = computed(
  (): ExpressionParseContext => ({
    roundIndex: 0,
    targets: targetStore.items,
    indices: indexStore.items,
    resources: resourceStore.items,
    simulationSettings: {
      roundUnit: settings.simulationRoundUnit,
      roundLength: settings.simulationRoundLength,
      roundAmount: settings.simulationRoundAmount,
      timeStart: settings.simulationTimeStart,
      durationType: settings.simulationDurationType
    },
    ...props.parseContext
  })
);

const expressionTree = ref(
  props.tree ?? useExpressionTree(expressionParseContext, props.expression)
);
const lastExpression = ref(props.expression);

// Listen for changes in the expression prop
watch(() => props.expression, (value) => {
  if (value == lastExpression.value) return;
  lastExpression.value = value;
  expressionTree.value.setExpression(value);
});

// ==============================
// INPUT MODE HANDLING

const mode = ref<"template" | "expression" | "tree">(props.inputMode);
const selectedMode = computed(() => {
  if (mode.value === "template") return "template";
  return "expression";
})

function setMode(newMode: "template" | "expression" | "tree") {
  mode.value = newMode;
  emit("update:input-mode", newMode);
}

function emitUpdate(value: string) {
  lastExpression.value = value;
  emit("update:expression", value);
}


// =============================================
// EXPRESSION EDITOR POPUP

const expressionEditorVisible = ref(false);

function setEditorOpen(value: boolean) {
  expressionEditorVisible.value = value;
}
</script>

<style lang="scss">
.expression-input-with-template {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
</style>
